import type { institutionTypes } from "../enums";

type InstitutionType = (typeof institutionTypes)[number];

/** Which follow-up queue a referral source is in. Here rather than beside its schema so the screen
 * can read it without loading zod. */
export const FOLLOW_UP_BUCKETS = ["overdue", "today", "week", "quiet", "unscheduled"] as const;
export type FollowUpBucket = (typeof FOLLOW_UP_BUCKETS)[number];

/** Prospecting vocabulary: what kind of place a source is, where the relationship stands, what
 * counts as a touch. */
export const REFERRAL_SOURCE_CATEGORIES = [
  "hospital",
  "physician_office",
  "clinic",
  "skilled_nursing",
  "home_health",
  "hospice",
  "assisted_living",
  "independent_living",
  "senior_center",
  "placement_agency",
  "pharmacy",
  "faith_community",
  "other",
] as const;
export type ReferralSourceCategory = (typeof REFERRAL_SOURCE_CATEGORIES)[number];

export const REFERRAL_SOURCE_CATEGORY_LABELS: Record<ReferralSourceCategory, string> = {
  hospital: "Hospital",
  physician_office: "Doctor's office",
  clinic: "Clinic",
  skilled_nursing: "Skilled nursing",
  home_health: "Home health",
  hospice: "Hospice",
  assisted_living: "Assisted living",
  independent_living: "Independent living",
  senior_center: "Senior center",
  placement_agency: "Placement agency",
  pharmacy: "Pharmacy",
  faith_community: "Faith community",
  other: "Other",
};

/** The categories that are the building's competition rather than its referrers. */
const COMPETITOR_CATEGORIES = new Set<string>(["assisted_living", "independent_living"]);

export const isCompetitorCategory = (category: string | null | undefined): boolean =>
  COMPETITOR_CATEGORIES.has(category ?? "");

/** Which book a pushed source is filed in: a doctor's office is a practice, so a clinic
 * institution; a pharmacy has its own. */
export const DIRECTORY_KIND_FOR_CATEGORY: Record<
  ReferralSourceCategory,
  { kind: "institution"; institutionType: InstitutionType } | { kind: "pharmacy" }
> = {
  hospital: { kind: "institution", institutionType: "hospital" },
  physician_office: { kind: "institution", institutionType: "clinic" },
  clinic: { kind: "institution", institutionType: "clinic" },
  skilled_nursing: { kind: "institution", institutionType: "skilled_nursing" },
  home_health: { kind: "institution", institutionType: "home_health" },
  hospice: { kind: "institution", institutionType: "hospice" },
  assisted_living: { kind: "institution", institutionType: "assisted_living" },
  independent_living: { kind: "institution", institutionType: "independent_living" },
  senior_center: { kind: "institution", institutionType: "other" },
  placement_agency: { kind: "institution", institutionType: "other" },
  pharmacy: { kind: "pharmacy" },
  faith_community: { kind: "institution", institutionType: "other" },
  other: { kind: "institution", institutionType: "other" },
};

/** The category an institution of this type reads as when it is pulled in. */
export function categoryForInstitutionType(type: InstitutionType): ReferralSourceCategory {
  const known: Partial<Record<InstitutionType, ReferralSourceCategory>> = {
    hospital: "hospital",
    clinic: "physician_office",
    skilled_nursing: "skilled_nursing",
    home_health: "home_health",
    hospice: "hospice",
    assisted_living: "assisted_living",
    independent_living: "independent_living",
    pharmacy: "pharmacy",
  };
  return known[type] ?? "other";
}

export const REFERRAL_STAGES = ["prospect", "contacted", "engaged", "active", "dormant"] as const;
export type ReferralStage = (typeof REFERRAL_STAGES)[number];
export const REFERRAL_STAGE_LABELS: Record<ReferralStage, string> = {
  prospect: "Prospect",
  contacted: "Contacted",
  engaged: "Engaged",
  active: "Referring",
  dormant: "Dormant",
};

export const REFERRAL_PRIORITIES = ["high", "medium", "low"] as const;

export const REFERRAL_TOUCH_KINDS = [
  "visit",
  "call",
  "email",
  "drop_off",
  "event",
  "note",
] as const;
export type ReferralTouchKind = (typeof REFERRAL_TOUCH_KINDS)[number];
export const REFERRAL_TOUCH_KIND_LABELS: Record<ReferralTouchKind, string> = {
  visit: "Visit",
  call: "Call",
  email: "Email",
  drop_off: "Drop-off",
  event: "Event",
  note: "Note",
};

/** The directory books a source can be the same row as. */
export const REFERRAL_LINK_KINDS = ["institution", "provider", "pharmacy", "faxContact"] as const;
export type ReferralLinkKind = (typeof REFERRAL_LINK_KINDS)[number];

interface LatLng {
  latitude: number;
  longitude: number;
}

const EARTH_RADIUS_MILES = 3958.8;
const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance, to a tenth of a mile. */
export function haversineMiles(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(h)) * 10) / 10;
}

const NAME_NOISE = new Set([
  "the",
  "of",
  "and",
  "inc",
  "llc",
  "llp",
  "pc",
  "pa",
  "md",
  "do",
  "dr",
  "center",
  "centre",
  "office",
  "offices",
  "group",
  "associates",
  "medical",
  "health",
  "healthcare",
  "clinic",
  "hospital",
  "pharmacy",
  "practice",
]);

/** Two spellings of one business share a key: "St. Luke's Hospital, Inc." and "st lukes
 * hospital". */
export function businessNameKey(raw: string | null | undefined): string {
  const words = String(raw ?? "")
    .toLowerCase()
    .replaceAll(/[’']/g, "")
    .replaceAll(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter((word) => word && !NAME_NOISE.has(word));
  return words.join(" ");
}
